import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Search, ScrollText, ChevronDown, ChevronRight, Undo2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Etichette leggibili + categoria colore per ogni azione nota.
const ACTION_META = {
  "appt.create_manual": { label: "Appuntamento creato manualmente", cat: "appt" },
  "appt.bulk_cancel": { label: "Annullamento appuntamenti in blocco", cat: "appt" },
  "opp.soft_delete": { label: "Opportunità eliminata", cat: "opp" },
  "piani_cura.opp_soft_delete": { label: "Opportunità eliminata", cat: "opp" },
  "piani_cura.opp_move": { label: "Opportunità spostata di fase", cat: "opp" },
  "piani_cura.opp_update": { label: "Opportunità aggiornata", cat: "opp" },
  "piani_cura.opp_restore": { label: "Opportunità ripristinata", cat: "opp" },
  "opportunity.repair": { label: "Opportunità riparata", cat: "opp" },
  "log.restore": { label: "Ripristino da log", cat: "opp" },
  "dashboard.manual_adjustment.upsert": { label: "Rettifica manuale dashboard", cat: "dash" },
  "feedback.delete": { label: "Feedback eliminato", cat: "fb" },
  "feedback.note_update": { label: "Nota feedback aggiornata", cat: "fb" },
  "feedback.unmatched_dismiss": { label: "Feedback non abbinato — ignorato", cat: "fb" },
  "feedback.unmatched_resolve": { label: "Feedback non abbinato — risolto", cat: "fb" },
  "feedback.backfill_ghl_notes": { label: "Sync note feedback su GHL", cat: "fb" },
  "feedback.backfill_ghl_notes.start": { label: "Sync note feedback — avvio", cat: "fb" },
  "piani_cura.pipeline_create": { label: "Pipeline creata", cat: "pipe" },
  "piani_cura.pipeline_delete": { label: "Pipeline eliminata", cat: "pipe" },
  "piani_cura.pipeline_rename": { label: "Pipeline rinominata", cat: "pipe" },
  "piani_cura.stage_create": { label: "Fase creata", cat: "pipe" },
  "piani_cura.stage_delete": { label: "Fase eliminata", cat: "pipe" },
  "piani_cura.stage_update": { label: "Fase aggiornata", cat: "pipe" },
  "user.flags_update": { label: "Flag utente aggiornati", cat: "user" },
  "user.permissions_update": { label: "Permessi utente aggiornati", cat: "user" },
  "user.toggle_active": { label: "Utente attivato/disattivato", cat: "user" },
  "user.delete": { label: "Utente eliminato", cat: "user" },
  "auth.login": { label: "Accesso effettuato", cat: "user" },
  "auth.logout": { label: "Disconnessione", cat: "user" },
  // Attività automatiche dei processi in background (attore = "sistema").
  "cron.appt_auto_cancel": { label: "Duplicati auto-cancellati", cat: "appt" },
  "cron.google_contacts_sync": { label: "Contatti Google sincronizzati", cat: "appt" },
  "cron.ghl_write_queue": { label: "Coda scritture GHL elaborata", cat: "opp" },
  "cron.ghl_write_queue_purge": { label: "Coda scritture GHL — pulizia", cat: "other" },
  "cron.multiappt_field": { label: "Multi-appuntamento — preparazione", cat: "appt" },
  "cron.multiappt_tag": { label: "Multi-appuntamento — promemoria inviati", cat: "appt" },
  "cron.purge_cancelled": { label: "Annullati di oggi eliminati", cat: "appt" },
  "cron.opp_history_tracking": { label: "Storico fasi aggiornato", cat: "opp" },
  "cron.pending_opportunities": { label: "Opportunità in sospeso sincronizzate", cat: "opp" },
  "cron.db_backup": { label: "Backup database su Drive", cat: "other" },
  "cron.photo_backup": { label: "Backup foto su Drive", cat: "other" },
  "cron.piani_cura_archive_purge": { label: "Archivio Piani di cura — pulizia", cat: "opp" },
};

// Aree per le attività catturate automaticamente (action = "auto.<metodo>.<area>").
const AUTO_AREAS = {
  "piani-cura": "Piani di cura", appointments: "Appuntamenti", opportunita: "Opportunità",
  "drive-backup": "Backup Drive", ghl: "GHL", dashboard: "Dashboard", feedback: "Feedback",
  admin: "Amministrazione", auth: "Account", invite: "Inviti utente",
  "google-calendar": "Google Calendar", "google-contacts": "Google Contatti",
  "google-business": "Google Business", multiappt: "Multi-appuntamento", promemoria: "Promemoria",
  indiretti: "Indiretti", preventivi: "Preventivi", "confronto-listini": "Confronto listini",
  treatments: "Prestazioni", sync: "Sincronizzazione", "video-tutorials": "Video tutorial",
  "ai-knowledge": "Base di conoscenza AI",
};
const AUTO_AREA_CAT = {
  "piani-cura": "opp", appointments: "appt", opportunita: "opp", "drive-backup": "other",
  ghl: "opp", dashboard: "dash", feedback: "fb", admin: "user", auth: "user", invite: "user",
  "google-calendar": "appt", "google-contacts": "appt", "google-business": "other",
  multiappt: "appt", promemoria: "appt", indiretti: "opp", preventivi: "opp",
  "confronto-listini": "dash", treatments: "opp", sync: "other", "video-tutorials": "other",
  "ai-knowledge": "other",
};
const AUTO_VERB = { post: "azione/creazione", put: "modifica", patch: "modifica", delete: "eliminazione" };

const CAT_CLS = {
  appt: "bg-blue-100 text-blue-700 border-blue-300",
  opp: "bg-violet-100 text-violet-700 border-violet-300",
  dash: "bg-cyan-100 text-cyan-700 border-cyan-300",
  fb: "bg-amber-100 text-amber-800 border-amber-300",
  pipe: "bg-emerald-100 text-emerald-700 border-emerald-300",
  user: "bg-rose-100 text-rose-700 border-rose-300",
  other: "bg-slate-100 text-slate-600 border-slate-300",
};

const metaFor = (a) => {
  if (ACTION_META[a]) return ACTION_META[a];
  // Attività automatica: "auto.<metodo>.<area>"
  if (typeof a === "string" && a.startsWith("auto.")) {
    const [, method, area] = a.split(".");
    const areaLabel = AUTO_AREAS[area] || "Sistema";
    return { label: `${areaLabel} — ${AUTO_VERB[method] || method}`, cat: AUTO_AREA_CAT[area] || "other" };
  }
  return { label: a, cat: "other" };
};

// Preferisci l'etichetta/categoria calcolata dal backend se presenti.
const metaForEntry = (e) => (e && e.label ? { label: e.label, cat: e.cat || "other" } : metaFor(e?.action));

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
};

export default function LogAttivitaPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(200);
  const [actionFilter, setActionFilter] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [restoring, setRestoring] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/audit-log`, {
        params: { limit, action: actionFilter || undefined },
        withCredentials: true,
      });
      setEntries(data?.entries || []);
    } catch {
      toast.error("Errore nel caricamento del log attività");
    } finally {
      setLoading(false);
    }
  }, [limit, actionFilter]);

  useEffect(() => { load(); }, [load]);

  const restore = async (e) => {
    const m = metaFor(e.action);
    const msg = e.action === "piani_cura.opp_move"
      ? "Riportare l'opportunità alla fase precedente?"
      : "Ripristinare questa opportunità eliminata? Verrà ricreata su GHL.";
    if (!window.confirm(`${m.label}\n\n${msg}`)) return;
    setRestoring((s) => ({ ...s, [e.id]: true }));
    try {
      const { data } = await axios.post(`${API}/piani-cura/log-restore`, { audit_id: e.id }, { withCredentials: true });
      toast.success(data?.kind === "stage_reverted" ? "Fase ripristinata ✓" : "Opportunità ripristinata ✓");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ripristino non riuscito");
    } finally {
      setRestoring((s) => ({ ...s, [e.id]: false }));
    }
  };

  const actionOptions = useMemo(() => {
    const set = new Set(entries.map((e) => e.action));
    Object.keys(ACTION_META).forEach((a) => set.add(a));
    return Array.from(set).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((e) => {
      const hay = [e.actor_email, e.action, metaForEntry(e).label, e.target_type, e.target_id, JSON.stringify(e.details || {})].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [entries, q]);

  return (
    <div className="max-w-6xl mx-auto px-4 pt-20 pb-10" data-testid="log-attivita-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <ScrollText className="w-6 h-6 text-slate-500" /> Log Attività
        </h1>
        <button
          type="button"
          onClick={load}
          data-testid="log-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm px-3 py-1.5 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Registro di tutte le attività svolte nell'applicazione: ogni creazione, modifica, spostamento, eliminazione, accesso e backup — chi ha fatto cosa, quando e su quale elemento.
      </p>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per utente, elemento, dettaglio…"
            data-testid="log-search-input"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
        </div>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          data-testid="log-action-filter"
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
        >
          <option value="">Tutte le attività</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>{metaFor(a).label}</option>
          ))}
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          data-testid="log-limit-filter"
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
        >
          {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>Ultime {n}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400" data-testid="log-loading">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-2xl" data-testid="log-empty">
          Nessuna attività trovata.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="log-list">
          {filtered.map((e, idx) => {
            const m = metaForEntry(e);
            const key = `${e.at}-${idx}`;
            const isOpen = expanded[key];
            const hasDetails = e.details && Object.keys(e.details).length > 0;
            return (
              <div key={key} className="rounded-xl border border-slate-200 bg-white" data-testid="log-row">
                <div className="flex items-start gap-2 px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => hasDetails && setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                    className="flex items-start gap-3 text-left flex-1 min-w-0"
                  >
                    <div className="w-5 pt-0.5 text-slate-400 shrink-0">
                      {hasDetails ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${CAT_CLS[m.cat]}`}>
                          {m.label}
                        </span>
                        <span className="text-sm text-slate-700 font-medium">{e.actor_email || "sistema"}</span>
                        {e.actor_role ? <span className="text-[11px] text-slate-400">({e.actor_role})</span> : null}
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5 tabular-nums">
                        {fmtDateTime(e.at)}
                        {e.target_type ? ` · ${e.target_type}` : ""}
                        {e.target_id ? `: ${e.target_id}` : ""}
                      </div>
                    </div>
                  </button>
                  {e.restorable ? (
                    <button
                      type="button"
                      onClick={() => restore(e)}
                      disabled={restoring[e.id]}
                      data-testid={`log-restore-${e.id}`}
                      className="inline-flex items-center gap-1.5 shrink-0 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium px-3 py-1.5 transition-colors disabled:opacity-60"
                    >
                      {restoring[e.id] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                      Ripristina
                    </button>
                  ) : (
                    <span
                      title="Questa azione non è ripristinabile automaticamente"
                      data-testid={`log-restore-disabled-${e.id}`}
                      className="inline-flex items-center gap-1.5 shrink-0 rounded-lg bg-slate-100 text-slate-400 text-xs font-medium px-3 py-1.5 cursor-not-allowed"
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Ripristina
                    </span>
                  )}
                </div>
                {isOpen && hasDetails ? (
                  <pre className="mx-4 mb-3 mt-0 bg-slate-50 border border-slate-100 rounded-lg p-3 text-[11px] text-slate-600 overflow-x-auto">
                    {JSON.stringify(e.details, null, 2)}
                  </pre>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
